import { Party } from "./Party";
import { PaymentFrequency } from "./PaymentFrequency";
import { Relationship } from "./Relationship";
import { DocumentType } from "./DocumentType";
import { ClaimPlan } from "../domain/ClaimPlan";
import { Medical } from "./Medical";
import { WellnessScreeningType } from "./WellnessScreeningType";
import { MannerOfDeath } from "./MannerOfDeath";
import { AffiliateBusinessUnit } from "./AffiliateBusinessUnit";
import { ConditionType } from "./ConditionType";
import { LeaveType } from "./LeaveType";
import { FormValues } from "../common/FormValues";
import { GenericRelationship } from "./GenericRelationship";

export enum BenefitType {
    PAID_FAMILY_AND_MEDICAL_LEAVE = "pfml",
    DISABILITY = "disability",
    HOSPITAL_INDEMNITY = "hospitalIndemnity",
    ACCIDENT = "accident",
    WELLNESS = "wellness",
    LIFE = "life",
    CRITICAL_ILLNESS = "criticalIllness"
}

export function findFullNameFor(benefitType: BenefitType): string {
    if(BenefitType.PAID_FAMILY_AND_MEDICAL_LEAVE === benefitType) {
        return "paid family and medical leave";
    } else if (BenefitType.DISABILITY === benefitType) {
        return "short- or long-term disability";
    } else if (BenefitType.HOSPITAL_INDEMNITY === benefitType) {
        return "hospital indemnity";
    } else if (BenefitType.ACCIDENT === benefitType) {
        return "accident";
    }  else if (BenefitType.CRITICAL_ILLNESS === benefitType) {
        return "critical illness/specified disease";
    } else if (BenefitType.WELLNESS === benefitType) {
        return "wellness/health screening";
    } else if (BenefitType.LIFE === benefitType) {
        return "life insurance";
    }
    return "";
}

export function findEmailNameFor(benefitType: BenefitType): string {
    const emailName = findFullNameFor(benefitType)
    if (BenefitType.PAID_FAMILY_AND_MEDICAL_LEAVE === benefitType) {
        return emailName + " (PFML)"
    }
    return emailName;
}

export function findTitleFor(benefitType: BenefitType): string {
    if(BenefitType.PAID_FAMILY_AND_MEDICAL_LEAVE === benefitType) {
        return "Paid Family and Medical Leave";
    } else if (BenefitType.DISABILITY === benefitType) {
        return "Short- or Long-Term Disability";
    } else if (BenefitType.HOSPITAL_INDEMNITY === benefitType) {
        return "Hospital Indemnity";
    } else if (BenefitType.ACCIDENT === benefitType) {
        return "Accident";
    } else if (BenefitType.CRITICAL_ILLNESS === benefitType) {
        return "Critical Illness/Specified Disease";
    } else if (BenefitType.WELLNESS === benefitType) {
        return "Wellness/Health Screening";
    } else if (BenefitType.LIFE === benefitType) {
        return "Life";
    }
    return "";
}

export function findAbbreviationFor(benefitType: BenefitType) {
    if (BenefitType.PAID_FAMILY_AND_MEDICAL_LEAVE === benefitType) {
        return "PFML";
    } else if (BenefitType.DISABILITY === benefitType) {
        return "Disability";
    } else if (BenefitType.HOSPITAL_INDEMNITY === benefitType) {
        return "Hospital Indemnity";
    } else if (BenefitType.ACCIDENT === benefitType) {
        return "Accident";
    } else if (BenefitType.CRITICAL_ILLNESS === benefitType) {
        return "Critical Illness/Specified Disease";
    } else if (BenefitType.WELLNESS === benefitType) {
        return "Wellness";
    } else if (BenefitType.LIFE === benefitType) {
        return "Life";
    }
    return "";
}

export function findHelpUrlFor(benefitType: BenefitType) {
    const url = "https://www.principal.com/businesses/protect-my-employees/"
    if (BenefitType.PAID_FAMILY_AND_MEDICAL_LEAVE === benefitType) {
        return url + "group-benefits/paid-family-medical-leave-state-disability-insurance";
    } else if (BenefitType.DISABILITY === benefitType) {
        return url + "group-benefits/disability-insurance";
    } else if (BenefitType.HOSPITAL_INDEMNITY === benefitType) {
        return url + "group-hospital-indemnity-insurance";
    } else if (BenefitType.WELLNESS === benefitType) {
        return "";
    } else if (BenefitType.LIFE === benefitType) {
        return url + "group-benefits/life-insurance";
    } else if (BenefitType.ACCIDENT === benefitType) {
        return url + "group-benefits/accident-insurance"
    } else if (BenefitType.CRITICAL_ILLNESS === benefitType) {
        return url + "group-benefits/critical-illness-insurance"
    }
    return "";
}

export function hasEmployeeStatement(values: FormValues) {
    return values.pfml || values.disability || values.hospitalIndemnity || values.wellness || values.accident || values.criticalIllness
}

export function hasPhysicianStatement(values: FormValues) {
    return values.disability
}

export function hasMedicalCertificationForm(values: FormValues) {
    return values.pfml
}

export function hasStrictDueDate(benefit: BenefitType) {
    return [BenefitType.HOSPITAL_INDEMNITY, BenefitType.DISABILITY, BenefitType.ACCIDENT, BenefitType.CRITICAL_ILLNESS].includes(benefit)
}

export function findPhysicianSectionNameFor(benefit: BenefitType) {
    let physicianStatementName = ''
    if (BenefitType.PAID_FAMILY_AND_MEDICAL_LEAVE === benefit) {
        physicianStatementName = DocumentType.MEDICAL_CERTIFICATION_FORM
    } else if (BenefitType.DISABILITY === benefit) {
        physicianStatementName = 'Physician section'
    }
    return physicianStatementName
}

export function findReviewOrCondolencesStatementFor(benefit: BenefitType) {
    if(BenefitType.LIFE === benefit) {
        return 'Please accept our condolences.'
    }
    return "To ensure prompt processing of your claim, we require a few additional documents."
}

export function findPartyChoicesFor(benefitType: BenefitType): Party[] {
    if ([BenefitType.PAID_FAMILY_AND_MEDICAL_LEAVE, BenefitType.DISABILITY].includes(benefitType)) {
        return [Party.EMPLOYEE, Party.EMPLOYER, Party.PHYSICIAN];
    } else if ([BenefitType.HOSPITAL_INDEMNITY, BenefitType.ACCIDENT, BenefitType.WELLNESS, BenefitType.CRITICAL_ILLNESS].includes(benefitType)) {
        return [Party.EMPLOYEE];
    } else if (BenefitType.LIFE === benefitType) {
        return [Party.BENEFICIARY, Party.EMPLOYER];
    }
    return [];
}

export function findRequiredPartiesFor(benefitType: BenefitType): Party[] {
    if ([BenefitType.PAID_FAMILY_AND_MEDICAL_LEAVE, BenefitType.DISABILITY].includes(benefitType)) {
        return [Party.EMPLOYEE, Party.EMPLOYER, Party.PHYSICIAN];
    } else if ([BenefitType.HOSPITAL_INDEMNITY, BenefitType.ACCIDENT, BenefitType.CRITICAL_ILLNESS].includes(benefitType)) {
        return [Party.EMPLOYEE, Party.EMPLOYER];
    } else if (BenefitType.WELLNESS === benefitType) {
        return [Party.EMPLOYEE];
    } else if (BenefitType.LIFE === benefitType) {
        return [Party.BENEFICIARY, Party.EMPLOYER];
    }
    return [];
}

export function findPaymentFrequencyFor(benefitType: BenefitType): PaymentFrequency {
    if ([BenefitType.PAID_FAMILY_AND_MEDICAL_LEAVE, BenefitType.DISABILITY].includes(benefitType)) {
        return PaymentFrequency.RECURRING;
    } else if ([BenefitType.HOSPITAL_INDEMNITY, BenefitType.ACCIDENT, BenefitType.WELLNESS, BenefitType.CRITICAL_ILLNESS].includes(benefitType)) {
        return PaymentFrequency.ADHOC;
    }
    return PaymentFrequency.RECURRING;
}

export function findIncludesStateNoticesFor(benefitType: BenefitType): boolean {
    return benefitType !== BenefitType.PAID_FAMILY_AND_MEDICAL_LEAVE;
}

export function findIncludesOptionalFormFor(benefitType: BenefitType): boolean {
    return benefitType === BenefitType.DISABILITY;
}

export function findPartiesWithOnlineAccessFor(benefitType: BenefitType): Party[] {
    if ([BenefitType.HOSPITAL_INDEMNITY, BenefitType.DISABILITY].includes(benefitType)) {
        return [Party.EMPLOYEE, Party.EMPLOYER];
    } else if ([BenefitType.PAID_FAMILY_AND_MEDICAL_LEAVE, BenefitType.ACCIDENT, BenefitType.CRITICAL_ILLNESS].includes(benefitType)) {
        return [Party.EMPLOYER]
    }
    return [];
}

export function findClaimTypeLabelFor(benefitType: BenefitType): string {
    if (BenefitType.PAID_FAMILY_AND_MEDICAL_LEAVE === benefitType) {
        return "Leave type";
    } else if ([BenefitType.DISABILITY, BenefitType.HOSPITAL_INDEMNITY, BenefitType.ACCIDENT, BenefitType.CRITICAL_ILLNESS].includes(benefitType)) {
        return "Condition type";
    } else if (BenefitType.WELLNESS === benefitType) {
        return "Screening type";
    } else if (BenefitType.LIFE === benefitType) {
        return "Manner of death";
    }
    return "Claim type";
}

type claimDateText = {
    headerText: string,
    detailsText: string,
    startDateLabel: string
}

export function findClaimDateFor(benefit: string | undefined): claimDateText {
    if (BenefitType.PAID_FAMILY_AND_MEDICAL_LEAVE === benefit) {
        return {
            headerText: "When would you like to take your leave?",
            detailsText: "Your leave can be taken all at once without breaks (continuous) or off-and-on (periodic). Add as many date ranges as needed.",
            startDateLabel: "Start date"
        };
    } else if (BenefitType.DISABILITY === benefit) {
        return {
            headerText: "When did/will your medical leave begin?",
            detailsText: "This is the first day you were/will be unable to work due to your medical condition.",
            startDateLabel: "Date"
        };
    } else if (BenefitType.HOSPITAL_INDEMNITY === benefit) {
        return {
            headerText: "Please provide the date your sickness started, injury happened, or date of delivery.",
            detailsText: "This should be the date of service from your bill, itemized receipt, or medical carrier's explanation of benefits.",
            startDateLabel: "Date"
        };
    } else if (BenefitType.ACCIDENT === benefit) {
        return {
            headerText: "Please provide the date your injury happened.",
            detailsText: "This should be the date of service from your bill, itemized receipt, or medical carrier's explanation of benefits.",
            startDateLabel: "Date"
        };
    } else if (BenefitType.CRITICAL_ILLNESS === benefit) {
        return {
            headerText: "When was your condition first diagnosed?",
            detailsText: "This should be the date of service from your bill, itemized receipt, or medical carrier's explanation of benefits.",
            startDateLabel: "Date"
        };
    } else if (BenefitType.WELLNESS === benefit) {
        return {
            headerText: "Please provide the date of service, test, or procedure.",
            detailsText: "This should be the date your physical, test, or procedure was performed.",
            startDateLabel: "Date"
        }
    } else if (BenefitType.LIFE === benefit) {
        return {
            headerText: "Please provide the date of death.",
            detailsText: "This should be the same date from the death certificate.",
            startDateLabel: "Date of death"
        }
    }
    return {headerText: "", detailsText: "", startDateLabel: ""};
}

export function findRelationshipsFor(values: FormValues) {
    const relationships = []
    if(values.genericRelationship !== GenericRelationship.FAMILY_MEMBER_OR_COVERED_DEPENDENT) {
        relationships.push(Relationship.EMPLOYEE)
    }
    const hasCoveredDependent = [values.hospitalIndemnity, values.accident, values.wellness, values.life, values.criticalIllness].includes(true);
    if(hasCoveredDependent) {
        relationships.push(Relationship.COVERED_DEPENDENT)
    }
    if(values.pfml && !hasCoveredDependent) {
        relationships.push(Relationship.FAMILY_MEMBER)
    }
    return relationships;
}

export function findEmailToFor(benefitType: BenefitType): string {
    if(BenefitType.LIFE === benefitType) {
        return process.env.LIFE_EMAIL_TO as string
    }
    return process.env.DISABILITY_EMAIL_TO as string
}

export function hasRequiredDocumentsFor(values: FormValues): boolean {
    return (values.hospitalIndemnity || values.accident || values.criticalIllness) && values.party === Party.EMPLOYEE;
}

export function findEmailConfirmationFor(benefitType: BenefitType): string {
    const employerEmailConfirmation = findRequiredPartiesFor(benefitType).includes(Party.EMPLOYER)
        ? " which will include a link to forward your employer with the information they'll need to complete your claim"
        : "";
    return `You'll receive an email confirmation from Principal${employerEmailConfirmation}.`;
}

export function findDocumentTypesFor(values: FormValues): DocumentType[] {
    let documentTypes = []
    if(values.pfml || values.disability || values.accident || values.criticalIllness) {
        documentTypes.push(DocumentType.PHYSICIAN_STATEMENT)
    }
    if(values.pfml) {
        documentTypes.push(DocumentType.BIRTH_CERTIFICATE, DocumentType.ADOPTION_FOSTER_PLACEMENT,
            DocumentType.ACTIVE_DUTY_ORDERS, DocumentType.LETTER_OF_IMPENDING_ACTIVATION, DocumentType.COPY_OF_CIVIL_COURT_APPOINTMENTS_OR_SERVICES);
    }
    if(values.life) {
        documentTypes.push(DocumentType.DEATH_CERTIFICATE, DocumentType.INCIDENT, DocumentType.AUTOPSY, DocumentType.NEWSPAPER,
            DocumentType.POLICE_DEPARTMENT_INFO, DocumentType.TRANSPORTATION_BILL, DocumentType.HIPAA_AUTH)
    }
    if(values.hospitalIndemnity) {
        documentTypes.push(DocumentType.BILLS, DocumentType.RECEIPTS, DocumentType.UNIFORM_BILL,
            DocumentType.EXPLANATION_OF_BENEFITS, DocumentType.ADMISSION, DocumentType.INCIDENT)
    }
    if(values.accident || values.criticalIllness) {
        documentTypes.push(DocumentType.ADMISSION, DocumentType.TREATMENT_NOTES, DocumentType.POLICE_DEPARTMENT_INFO)
    }
    return documentTypes;
}

export function findDefaultLeaveTypesFor(benefitType: BenefitType): ClaimPlan[] {
    if (benefitType === BenefitType.PAID_FAMILY_AND_MEDICAL_LEAVE) {
        return [{
            leaveType: LeaveType.SERIOUS_HEALTH_CONDITION,
            relationship: [Relationship.EMPLOYEE, Relationship.FAMILY_MEMBER],
            medical: [Medical.MEDICAL]
        }, {
            leaveType: LeaveType.MATERNITY_LEAVE,
            relationship: [Relationship.EMPLOYEE],
            medical: [Medical.MEDICAL]
        }, {
            leaveType: LeaveType.BONDING_WITH_A_CHILD,
            relationship: [Relationship.FAMILY_MEMBER],
            medical: [Medical.NON_MEDICAL]
        }, {
            leaveType: LeaveType.NICU_CARE,
            relationship: [Relationship.FAMILY_MEMBER],
            medical: [Medical.NON_MEDICAL]
        }, {
            leaveType: LeaveType.ACTIVE_DUTY_OR_MILITARY_EVENTS,
            relationship: [Relationship.FAMILY_MEMBER],
            medical: [Medical.MEDICAL, Medical.NON_MEDICAL]
        }, {
            leaveType: LeaveType.PERSONAL_OR_FAMILY_VIOLENCE,
            relationship: [Relationship.EMPLOYEE],
            medical: [Medical.MEDICAL, Medical.NON_MEDICAL]
        }] as ClaimPlan[];
    } else if (benefitType === BenefitType.DISABILITY) {
        return Object.values(ConditionType).map((conditionType: ConditionType) => {
            return {
                conditionType,
                relationship: [Relationship.EMPLOYEE],
                medical: [Medical.MEDICAL]
            };
        })
    } else if (benefitType === BenefitType.HOSPITAL_INDEMNITY) {
        return Object.values(ConditionType).map((conditionType: ConditionType) => {
            return {
                conditionType,
                relationship: [Relationship.EMPLOYEE, Relationship.COVERED_DEPENDENT],
                medical: [Medical.MEDICAL]
            };
        })
    } else if (benefitType === BenefitType.ACCIDENT) {
        return [{
            conditionType: ConditionType.INJURY,
            relationship: [Relationship.EMPLOYEE, Relationship.COVERED_DEPENDENT],
            medical: [Medical.MEDICAL]
        }]
    } else if (benefitType === BenefitType.CRITICAL_ILLNESS) {
        return [{
            conditionType: ConditionType.SICKNESS,
            relationship: [Relationship.EMPLOYEE, Relationship.COVERED_DEPENDENT],
            medical: [Medical.MEDICAL]
        }]
    } else if (benefitType === BenefitType.WELLNESS) {
        return Object.values(WellnessScreeningType).map((screeningType: WellnessScreeningType) => {
            return {
                screeningType,
                relationship: [Relationship.EMPLOYEE, Relationship.COVERED_DEPENDENT],
                medical: [Medical.MEDICAL]
            };
        });
    } else if (benefitType === BenefitType.LIFE) {
        return Object.values(MannerOfDeath).map((mannerOfDeath: MannerOfDeath) => {
            return {
                mannerOfDeath,
                relationship: [Relationship.EMPLOYEE, Relationship.COVERED_DEPENDENT, '' as Relationship],
                medical: [Medical.NON_MEDICAL]
            };
        });
    }
    return [];
}

export function findAffiliateBusinessUnitCodesFor(benefitType: BenefitType): AffiliateBusinessUnit[] {
    let businessUnitCodes = [] as AffiliateBusinessUnit[];
    if(BenefitType.PAID_FAMILY_AND_MEDICAL_LEAVE === benefitType) {
        businessUnitCodes = [AffiliateBusinessUnit.PAID_FAMILY_LEAVE, AffiliateBusinessUnit.PAID_MEDICAL_LEAVE]
    } else if(BenefitType.DISABILITY === benefitType) {
        businessUnitCodes = [AffiliateBusinessUnit.SHORT_TERM_DISABILITY, AffiliateBusinessUnit.VOLUNTARY_SHORT_TERM_DISABILITY,
            AffiliateBusinessUnit.SELF_FUNDED_SHORT_TERM_DISABILITY, AffiliateBusinessUnit.LONG_TERM_DISABILITY, AffiliateBusinessUnit.VOLUNTARY_LONG_TERM_DISABILITY,];
    } else if(BenefitType.HOSPITAL_INDEMNITY === benefitType) {
        businessUnitCodes = [AffiliateBusinessUnit.HOSPITAL_INDEMNITY, AffiliateBusinessUnit.VOLUNTARY_HOSPITAL_INDEMNITY,
            AffiliateBusinessUnit.VOLUNTARY_HOSPITAL_INDEMNITY_THREE, AffiliateBusinessUnit.VOLUNTARY_HOSPITAL_INDEMNITY_TWO]
    } else if(BenefitType.ACCIDENT === benefitType) {
        businessUnitCodes = [AffiliateBusinessUnit.ACCIDENT]
    } else if(BenefitType.CRITICAL_ILLNESS === benefitType) {
        businessUnitCodes = [AffiliateBusinessUnit.CRITICAL_ILLNESS]
    } else if(BenefitType.WELLNESS === benefitType) {
        businessUnitCodes = [AffiliateBusinessUnit.ACCIDENT, AffiliateBusinessUnit.CRITICAL_ILLNESS, AffiliateBusinessUnit.HOSPITAL_INDEMNITY,
            AffiliateBusinessUnit.VOLUNTARY_HOSPITAL_INDEMNITY, AffiliateBusinessUnit.VOLUNTARY_HOSPITAL_INDEMNITY_THREE,
            AffiliateBusinessUnit.VOLUNTARY_HOSPITAL_INDEMNITY_TWO]
    } else if(BenefitType.LIFE === benefitType) {
        businessUnitCodes = [AffiliateBusinessUnit.GROUP_TERM_LIFE, AffiliateBusinessUnit.VOLUNTARY_TERM_LIFE]
    }
    businessUnitCodes.push(AffiliateBusinessUnit.SBD_COMPASS);
    return businessUnitCodes;
}
